import { useState, useMemo, useEffect, useRef, Fragment } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Trash2,
  ChevronRight,
  ChevronLeft,
  FolderPlus,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Textarea } from '@/app/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Checkbox } from '@/app/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Label } from '@/app/components/ui/label';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Alert,
  AlertDescription,
} from '@/app/components/ui/alert';
import {
  useClients,
  useCreateClient,
  useCreateBid,
  useBid,
  useUpdateBid,
  useBidAvailability,
} from '@/hooks/use-projects';
import { useGearRates } from '@/hooks/use-gear-rates';
import { useIndustry } from '@/hooks/use-industry';
import { useOrgCurrency } from '@/hooks/use-settings';
import { formatMoney } from '@/lib/currency';
import type { BidLineType, BidGearRateUnit, BidGearLine, GearRate } from '@/lib/types';
import { formatDateOnly } from '@/lib/date-utils';

const RATE_UNITS: { value: BidGearRateUnit; label: string }[] = [
  { value: 'per_day', label: 'Per Day' },
  { value: 'per_week', label: 'Per Week' },
  { value: 'per_job', label: 'Per Job' },
  { value: 'per_item', label: 'Per Item' },
];

const LINE_TYPES: { value: BidLineType; label: string }[] = [
  { value: 'labor', label: 'Labor' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'materials', label: 'Materials' },
  { value: 'travel', label: 'Travel' },
  { value: 'per_diem', label: 'Per Diem' },
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'other', label: 'Other' },
];

const TEMPLATES = [
  {
    name: 'Standard Rope Access Day Rate',
    items: [
      { line_type: 'labor' as BidLineType, description: 'Rope Access Technician (Level 3)', quantity: '1', unit: 'day', unit_price: '650' },
      { line_type: 'labor' as BidLineType, description: 'Rope Access Technician (Level 1)', quantity: '1', unit: 'day', unit_price: '450' },
      { line_type: 'labor' as BidLineType, description: 'Safety Watch', quantity: '1', unit: 'day', unit_price: '350' },
      { line_type: 'equipment' as BidLineType, description: 'Rope Access Equipment Package', quantity: '1', unit: 'day', unit_price: '150' },
    ],
  },
  {
    name: 'Mobilization Package',
    items: [
      { line_type: 'travel' as BidLineType, description: 'Mobilization / Demobilization', quantity: '1', unit: 'lot', unit_price: '2500' },
      { line_type: 'travel' as BidLineType, description: 'Airfare (round trip)', quantity: '2', unit: 'ticket', unit_price: '500' },
      { line_type: 'per_diem' as BidLineType, description: 'Per Diem (meals & incidentals)', quantity: '5', unit: 'day', unit_price: '75' },
      { line_type: 'travel' as BidLineType, description: 'Ground Transportation', quantity: '1', unit: 'lot', unit_price: '300' },
    ],
  },
];

const DEFAULT_TERMS = `1. This estimate is valid for 30 days from the date of issue.
2. Payment terms: Net 30 days from invoice date.
3. All work will be performed in accordance with SPRAT/IRATA standards.
4. Client is responsible for providing site access and any necessary permits.
5. Any additional work beyond the scope described above will be quoted separately.
6. Cancellation within 48 hours of scheduled mobilization may incur cancellation fees.
7. Insurance certificates available upon request.`;

interface LineItemRow {
  tempId: string;
  sectionTempId: string | null;
  line_type: BidLineType;
  description: string;
  quantity: string;
  unit: string;
  unit_price: string;
  order: number;
}

interface SectionRow {
  tempId: string;
  title: string;
  order: number;
}

interface GearAdjustmentRow {
  tempId: string;
  adjustment_type: 'allowance' | 'discount';
  description: string;
  amount: string;
  quantity: string;
  is_per_unit: boolean;
}

interface GearLineRow {
  tempId: string;
  serverId?: number;
  description: string;
  gearRateId: string;
  gearModelId: string;
  quantity: string;
  baseRate: string;
  overrideRate: string;
  rateUnit: BidGearRateUnit;
  startDate: string;
  endDate: string;
  notes: string;
  adjustments: GearAdjustmentRow[];
}

function generateId() {
  return Math.random().toString(36).substring(2, 11);
}

function mapGearRateUnit(rateUnit?: string | null): BidGearRateUnit {
  const valid: BidGearRateUnit[] = ['per_day', 'per_week', 'per_job', 'per_item'];
  if (rateUnit && valid.includes(rateUnit as BidGearRateUnit)) {
    return rateUnit as BidGearRateUnit;
  }
  if (rateUnit === 'daily' || rateUnit === 'hourly') {
    return 'per_day';
  }
  return 'per_day';
}

const HOURS_PER_DAY = 8;

function normalizeGearRate(rate: GearRate): { baseRate: string; rateUnit: BidGearRateUnit } {
  const rateUnit = mapGearRateUnit(rate.rate_unit);
  const raw = toNumber(rate.rate);
  const value = rate.rate_unit === 'hourly' ? raw * HOURS_PER_DAY : raw;
  return { baseRate: value.toFixed(2), rateUnit };
}

function parseDateString(value: string): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

function toNumber(value: string): number {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function billingUnits(
  rateUnit: BidGearRateUnit,
  startDate: string,
  endDate: string,
  fallbackStartDate: string,
  fallbackEndDate: string,
): number {
  if (rateUnit === 'per_job' || rateUnit === 'per_item') {
    return 1;
  }

  const start = parseDateString(startDate || fallbackStartDate);
  const end = parseDateString(endDate || fallbackEndDate);
  if (!start || !end) {
    return 1;
  }

  const startUtc = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const endUtc = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  let days = Math.floor((endUtc - startUtc) / (1000 * 60 * 60 * 24)) + 1;
  if (days < 1) days = 1;

  if (rateUnit === 'per_week') {
    return Math.ceil(days / 7);
  }
  return days;
}

function calculateGearLineTotal(
  line: GearLineRow,
  fallbackStartDate: string,
  fallbackEndDate: string,
): number {
  const qty = toNumber(line.quantity);
  const rate = line.overrideRate.trim() !== ''
    ? toNumber(line.overrideRate)
    : toNumber(line.baseRate);
  const units = billingUnits(line.rateUnit, line.startDate, line.endDate, fallbackStartDate, fallbackEndDate);
  let base = qty * rate * units;

  for (const adj of line.adjustments) {
    const adjQty = toNumber(adj.quantity);
    const unitsMult = adj.is_per_unit ? adjQty : 1;
    const amount = toNumber(adj.amount) * unitsMult;
    base -= amount;
  }

  return base;
}

function gearLineToPayload(line: GearLineRow): BidGearLine {
  return {
    description: line.description,
    gear_rate: line.gearRateId ? parseInt(line.gearRateId, 10) : null,
    gear_model: line.gearModelId ? parseInt(line.gearModelId, 10) : null,
    quantity: line.quantity,
    base_rate: line.baseRate,
    override_rate: line.overrideRate.trim() !== '' ? line.overrideRate : null,
    rate_unit: line.rateUnit,
    start_date: line.startDate || null,
    end_date: line.endDate || null,
    notes: line.notes,
    adjustments: line.adjustments.map((adj) => ({
      adjustment_type: adj.adjustment_type,
      description: adj.description,
      amount: adj.amount,
      quantity: adj.quantity,
      is_per_unit: adj.is_per_unit,
    })),
  } as BidGearLine;
}

export function CreateBidPage() {
  const currency = useOrgCurrency();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const editBidId = id ? parseInt(id, 10) : null;
  const isEdit = editBidId !== null && editBidId > 0;

  const createBidMutation = useCreateBid();
  const updateBidMutation = useUpdateBid();
  const createClientMutation = useCreateClient();
  const { data: clientsData, isLoading: clientsLoading } = useClients({ page_size: 100 });
  const { data: existingBid, isLoading: bidLoading } = useBid(isEdit ? editBidId! : 0);
  const { data: gearRatesData, isLoading: gearRatesLoading } = useGearRates({ page_size: 100 });
  const { data: availability } = useBidAvailability(isEdit ? editBidId! : 0);
  const { labels } = useIndustry();
  const clients = clientsData?.results || [];
  const gearRates = gearRatesData?.results || [];

  const availabilityMap = useMemo(() => {
    const map = new Map<number, (typeof availability) extends undefined ? never : NonNullable<typeof availability>[number]>();
    if (availability) {
      for (const row of availability) {
        map.set(row.id, row);
      }
    }
    return map;
  }, [availability]);

  const STEP_LABELS = [`${labels.client} & Basics`, 'Line Items & Gear', 'Terms & Conditions', 'Review'];

  const [step, setStep] = useState(0);

  // Step 1: Client & Basics
  const [clientId, setClientId] = useState<number | null>(null);
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientContact, setNewClientContact] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [estimatedStartDate, setEstimatedStartDate] = useState('');
  const [estimatedEndDate, setEstimatedEndDate] = useState('');

  // Step 2: Line Items & Gear
  const [sections, setSections] = useState<SectionRow[]>([]);
  const [lineItems, setLineItems] = useState<LineItemRow[]>([]);
  const [gearLines, setGearLines] = useState<GearLineRow[]>([]);
  const [taxRate, setTaxRate] = useState('0');

  // Step 3: Terms
  const [termsAndConditions, setTermsAndConditions] = useState(DEFAULT_TERMS);
  const [notes, setNotes] = useState('');

  const initializedRef = useRef(false);

  useEffect(() => {
    if (!isEdit || !existingBid || initializedRef.current) return;
    initializedRef.current = true;

    setClientId(existingBid.client);
    setTitle(existingBid.title);
    setDescription(existingBid.description || '');
    setValidUntil(existingBid.valid_until || '');
    setEstimatedStartDate(existingBid.estimated_start_date || '');
    setEstimatedEndDate(existingBid.estimated_end_date || '');
    setTaxRate(existingBid.tax_rate);
    setTermsAndConditions(existingBid.terms_and_conditions || '');
    setNotes(existingBid.notes || '');

    const sectionIdToTempId = new Map<number | null, string>();
    const mappedSections: SectionRow[] = (existingBid.sections || []).map((s) => {
      const tempId = generateId();
      sectionIdToTempId.set(s.id, tempId);
      return { tempId, title: s.title, order: s.order };
    });
    setSections(mappedSections);

    const mappedLineItems: LineItemRow[] = (existingBid.line_items || []).map((li, i) => ({
      tempId: generateId(),
      sectionTempId: li.section ? sectionIdToTempId.get(li.section) ?? null : null,
      line_type: li.line_type,
      description: li.description,
      quantity: li.quantity,
      unit: li.unit,
      unit_price: li.unit_price,
      order: li.order ?? i,
    }));
    setLineItems(mappedLineItems);

    const mappedGearLines: GearLineRow[] = (existingBid.bid_gear_lines || []).map((line, i) => ({
      tempId: generateId(),
      serverId: line.id,
      description: line.description,
      gearRateId: line.gear_rate?.toString() || '',
      gearModelId: line.gear_model?.toString() || '',
      quantity: line.quantity,
      baseRate: line.base_rate,
      overrideRate: line.override_rate ?? '',
      rateUnit: line.rate_unit,
      startDate: line.start_date || '',
      endDate: line.end_date || '',
      notes: line.notes || '',
      adjustments: (line.adjustments || []).map((adj) => ({
        tempId: generateId(),
        adjustment_type: adj.adjustment_type,
        description: adj.description,
        amount: adj.amount,
        quantity: adj.quantity,
        is_per_unit: adj.is_per_unit,
      })),
      order: i,
    }));
    setGearLines(mappedGearLines);
  }, [isEdit, existingBid]);

  const lineSubtotal = useMemo(() => {
    return lineItems.reduce((sum, item) => {
      const qty = toNumber(item.quantity);
      const price = toNumber(item.unit_price);
      return sum + qty * price;
    }, 0);
  }, [lineItems]);

  const gearSubtotal = useMemo(() => {
    return gearLines.reduce(
      (sum, line) => sum + calculateGearLineTotal(line, estimatedStartDate, estimatedEndDate),
      0,
    );
  }, [gearLines, estimatedStartDate, estimatedEndDate]);

  const subtotal = lineSubtotal + gearSubtotal;

  const taxAmount = useMemo(() => {
    return subtotal * toNumber(taxRate) / 100;
  }, [subtotal, taxRate]);

  const total = subtotal + taxAmount;

  const handleCreateClient = async () => {
    if (!newClientName) return;
    try {
      const client = await createClientMutation.mutateAsync({
        name: newClientName,
        contact_name: newClientContact,
        contact_email: newClientEmail,
        contact_phone: newClientPhone,
      });
      setClientId(client.id);
      setShowNewClient(false);
      setNewClientName('');
      setNewClientContact('');
      setNewClientEmail('');
      setNewClientPhone('');
    } catch {
      // error handled by mutation
    }
  };

  const addSection = () => {
    setSections([...sections, { tempId: generateId(), title: '', order: sections.length }]);
  };

  const removeSection = (tempId: string) => {
    setSections(sections.filter((s) => s.tempId !== tempId));
    setLineItems(lineItems.map((li) =>
      li.sectionTempId === tempId ? { ...li, sectionTempId: null } : li
    ));
  };

  const updateSection = (tempId: string, title: string) => {
    setSections(sections.map((s) => (s.tempId === tempId ? { ...s, title } : s)));
  };

  const addLineItem = (sectionTempId: string | null = null) => {
    setLineItems([
      ...lineItems,
      {
        tempId: generateId(),
        sectionTempId,
        line_type: 'labor',
        description: '',
        quantity: '1',
        unit: 'day',
        unit_price: '0',
        order: lineItems.length,
      },
    ]);
  };

  const removeLineItem = (tempId: string) => {
    setLineItems(lineItems.filter((li) => li.tempId !== tempId));
  };

  const updateLineItem = (tempId: string, field: keyof LineItemRow, value: string) => {
    setLineItems(
      lineItems.map((li) => (li.tempId === tempId ? { ...li, [field]: value } : li))
    );
  };

  const applyTemplate = (templateIndex: number) => {
    const template = TEMPLATES[templateIndex];
    const newItems: LineItemRow[] = template.items.map((item, i) => ({
      tempId: generateId(),
      sectionTempId: null,
      ...item,
      order: lineItems.length + i,
    }));
    setLineItems([...lineItems, ...newItems]);
  };

  const addGearLine = () => {
    setGearLines([
      ...gearLines,
      {
        tempId: generateId(),
        description: '',
        gearRateId: '',
        gearModelId: '',
        quantity: '1',
        baseRate: '0',
        overrideRate: '',
        rateUnit: 'per_day',
        startDate: '',
        endDate: '',
        notes: '',
        adjustments: [],
      },
    ]);
  };

  const removeGearLine = (tempId: string) => {
    setGearLines(gearLines.filter((l) => l.tempId !== tempId));
  };

  const updateGearLine = (
    tempId: string,
    field: keyof GearLineRow,
    value: string | number | GearAdjustmentRow[]
  ) => {
    setGearLines(
      gearLines.map((l) => {
        if (l.tempId !== tempId) return l;

        if (field === 'gearRateId' && typeof value === 'string') {
          const selectedRateId = value;
          const rate = gearRates.find((r) => r.id.toString() === selectedRateId);
          const normalized = rate ? normalizeGearRate(rate) : null;
          return {
            ...l,
            gearRateId: value,
            baseRate: normalized ? normalized.baseRate : l.baseRate,
            rateUnit: normalized ? normalized.rateUnit : l.rateUnit,
            gearModelId: rate?.gear_model?.toString() || l.gearModelId,
          };
        }

        return { ...l, [field]: value };
      })
    );
  };

  const addGearAdjustment = (lineTempId: string) => {
    setGearLines(
      gearLines.map((l) =>
        l.tempId === lineTempId
          ? {
              ...l,
              adjustments: [
                ...l.adjustments,
                {
                  tempId: generateId(),
                  adjustment_type: 'discount',
                  description: '',
                  amount: '0',
                  quantity: '1',
                  is_per_unit: false,
                },
              ],
            }
          : l
      )
    );
  };

  const updateGearAdjustment = (
    lineTempId: string,
    adjTempId: string,
    field: keyof GearAdjustmentRow,
    value: string | boolean
  ) => {
    setGearLines(
      gearLines.map((l) => {
        if (l.tempId !== lineTempId) return l;
        return {
          ...l,
          adjustments: l.adjustments.map((adj) =>
            adj.tempId === adjTempId ? { ...adj, [field]: value } : adj
          ),
        };
      })
    );
  };

  const removeGearAdjustment = (lineTempId: string, adjTempId: string) => {
    setGearLines(
      gearLines.map((l) =>
        l.tempId === lineTempId
          ? { ...l, adjustments: l.adjustments.filter((a) => a.tempId !== adjTempId) }
          : l
      )
    );
  };

  const handleSubmit = async () => {
    if (!clientId || !title) return;

    const sectionPayload = sections
      .filter((s) => s.title.trim())
      .map((s, i) => ({
        title: s.title,
        order: i,
        _tempId: s.tempId,
      }));

    const sectionTempIdToIndex = new Map<string, number>();
    sectionPayload.forEach((s, i) => {
      sectionTempIdToIndex.set(s._tempId, i);
    });

    const lineItemPayload = lineItems.map((li, i) => ({
      section: li.sectionTempId ? sectionTempIdToIndex.get(li.sectionTempId) ?? null : null,
      line_type: li.line_type,
      description: li.description,
      quantity: li.quantity,
      unit: li.unit,
      unit_price: li.unit_price,
      order: i,
    }));

    const bidGearLinesPayload = gearLines.map((line, i) => ({
      ...gearLineToPayload(line),
      line_order: i,
    }));

    const payload = {
      client: clientId,
      title,
      description,
      valid_until: validUntil || null,
      estimated_start_date: estimatedStartDate || null,
      estimated_end_date: estimatedEndDate || null,
      tax_rate: taxRate,
      notes,
      terms_and_conditions: termsAndConditions,
      sections: sectionPayload.map(({ _tempId: _, ...rest }) => rest),
      line_items: lineItemPayload,
      bid_gear_lines: bidGearLinesPayload,
    };

    try {
      let bid;
      if (isEdit && editBidId) {
        bid = await updateBidMutation.mutateAsync({ id: editBidId, data: payload });
      } else {
        bid = await createBidMutation.mutateAsync(payload);
      }
      navigate(`/projects/bids/${bid.id}`);
    } catch {
      // error handled by mutation
    }
  };

  const canProceed = () => {
    switch (step) {
      case 0:
        return clientId !== null && title.trim() !== '';
      case 1:
        if (lineItems.length === 0 && gearLines.length === 0) return false;
        return (
          lineItems.every((li) => li.description.trim() !== '') &&
          gearLines.every((l) => l.description.trim() !== '')
        );
      case 2:
        return true;
      case 3:
        return true;
      default:
        return false;
    }
  };

  const selectedClient = clients.find((c) => c.id === clientId);

  if (isEdit && bidLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/projects/bids')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit Bid / Estimate' : 'New Bid / Estimate'}
          </h1>
          <p className="text-gray-600">
            {isEdit ? 'Update estimate details' : `Create a new bid for your ${labels.client.toLowerCase()}`}
          </p>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {STEP_LABELS.map((label, i) => (
          <div key={i} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => i < step && setStep(i)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                i === step
                  ? 'bg-blue-600 text-white'
                  : i < step
                    ? 'bg-blue-100 text-blue-700 cursor-pointer hover:bg-blue-200'
                    : 'bg-gray-100 text-gray-400'
              }`}
            >
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs bg-white/20">
                {i + 1}
              </span>
              <span className="hidden sm:inline">{label}</span>
            </button>
            {i < STEP_LABELS.length - 1 && (
              <ChevronRight className="h-4 w-4 text-gray-300" />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Client & Basics */}
      {step === 0 && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Select {labels.client}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {clientsLoading ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Select
                  value={clientId?.toString() || ''}
                  onValueChange={(v) => {
                    if (v === '__new__') {
                      setShowNewClient(true);
                      setClientId(null);
                    } else {
                      setClientId(parseInt(v, 10));
                      setShowNewClient(false);
                    }
                  }}
                >
                  <SelectTrigger aria-label={`Choose a ${labels.client.toLowerCase()}`}>
                    <SelectValue placeholder={`Choose a ${labels.client.toLowerCase()}...`} />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id.toString()}>
                        {c.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="__new__">+ Create New {labels.client}</SelectItem>
                  </SelectContent>
                </Select>
              )}

              {showNewClient && (
                <div id="new-bid-client-fields" className="border rounded-lg p-4 space-y-3 bg-gray-50">
                  <h4 className="font-medium text-sm">New {labels.client}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Company Name *</Label>
                      <Input
                        value={newClientName}
                        onChange={(e) => setNewClientName(e.target.value)}
                        placeholder="Apex Rope Access"
                        required
                      />
                    </div>
                    <div>
                      <Label>Contact Name</Label>
                      <Input
                        value={newClientContact}
                        onChange={(e) => setNewClientContact(e.target.value)}
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={newClientEmail}
                        onChange={(e) => setNewClientEmail(e.target.value)}
                        placeholder="john@apexropeaccess.com"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={newClientPhone}
                        onChange={(e) => setNewClientPhone(e.target.value)}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    data-validate-required="true"
                    data-validation-scope="new-bid-client-fields"
                    onClick={handleCreateClient}
                    disabled={createClientMutation.isPending}
                  >
                    {createClientMutation.isPending ? 'Creating...' : `Create ${labels.client}`}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Bid Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Window Cleaning — Building A"
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Brief description of the work..."
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="validUntil">Valid Until</Label>
                  <Input
                    id="validUntil"
                    type="date"
                    value={validUntil}
                    onChange={(e) => setValidUntil(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="estimatedStartDate">Estimated Start</Label>
                  <Input
                    id="estimatedStartDate"
                    type="date"
                    value={estimatedStartDate}
                    onChange={(e) => setEstimatedStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="estimatedEndDate">Estimated End</Label>
                  <Input
                    id="estimatedEndDate"
                    type="date"
                    value={estimatedEndDate}
                    onChange={(e) => setEstimatedEndDate(e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 2: Line Items & Gear */}
      {step === 1 && (
        <div className="space-y-6">
          {/* Templates */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Templates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {TEMPLATES.map((t, i) => (
                  <Button
                    key={i}
                    variant="outline"
                    size="sm"
                    onClick={() => applyTemplate(i)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {t.name}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Sections & Line Items */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Line Items</CardTitle>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={addSection}>
                  <FolderPlus className="h-4 w-4 mr-1" />
                  Add Section
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => addLineItem()}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Item
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {sections.map((section) => (
                <div key={section.tempId} className="border rounded-lg">
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-t-lg">
                    <Input
                      value={section.title}
                      onChange={(e) => updateSection(section.tempId, e.target.value)}
                      placeholder="Section title..."
                      className="font-medium"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => addLineItem(section.tempId)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeSection(section.tempId)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  <div className="p-3 space-y-2">
                    {lineItems
                      .filter((li) => li.sectionTempId === section.tempId)
                      .map((item) => (
                        <LineItemEditor
                          key={item.tempId}
                          item={item}
                          onUpdate={updateLineItem}
                          onRemove={removeLineItem}
                        />
                      ))}
                    {lineItems.filter((li) => li.sectionTempId === section.tempId).length === 0 && (
                      <p className="text-sm text-gray-400 text-center py-2">
                        No items in this section. Click + to add.
                      </p>
                    )}
                  </div>
                </div>
              ))}

              {/* Unsectioned items */}
              {lineItems.filter((li) => li.sectionTempId === null).length > 0 && (
                <div className="space-y-2">
                  {sections.length > 0 && (
                    <p className="text-sm font-medium text-gray-500 px-1">Unsectioned Items</p>
                  )}
                  {lineItems
                    .filter((li) => li.sectionTempId === null)
                    .map((item) => (
                      <LineItemEditor
                        key={item.tempId}
                        item={item}
                        onUpdate={updateLineItem}
                        onRemove={removeLineItem}
                      />
                    ))}
                </div>
              )}

              {lineItems.length === 0 && sections.length === 0 && (
                <p className="text-gray-500 text-center py-8">
                  No line items yet. Add items manually or use a template above.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Gear Estimate */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Gear Estimate</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addGearLine}>
                <Plus className="h-4 w-4 mr-1" />
                Add Gear Line
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {gearRatesLoading ? (
                <Skeleton className="h-24 w-full" />
              ) : gearLines.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No gear estimate lines yet. Click Add Gear Line to estimate rental gear.
                </p>
              ) : (
                gearLines.map((line) => (
                  <GearLineEditor
                    key={line.tempId}
                    line={line}
                    gearRates={gearRates}
                    availability={line.serverId ? availabilityMap.get(line.serverId) : undefined}
                    estimateStartDate={estimatedStartDate}
                    estimateEndDate={estimatedEndDate}
                    onUpdate={updateGearLine}
                    onRemove={removeGearLine}
                    onAddAdjustment={addGearAdjustment}
                    onUpdateAdjustment={updateGearAdjustment}
                    onRemoveAdjustment={removeGearAdjustment}
                  />
                ))
              )}
            </CardContent>
          </Card>

          {/* Totals */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-end gap-2">
                {lineItems.length > 0 && (
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-600">Line Items Subtotal:</span>
                    <span className="font-medium w-32 text-right">{formatMoney(lineSubtotal, currency)}</span>
                  </div>
                )}
                {gearLines.length > 0 && (
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-600">Gear Subtotal:</span>
                    <span className="font-medium w-32 text-right">{formatMoney(gearSubtotal, currency)}</span>
                  </div>
                )}
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium w-32 text-right">{formatMoney(subtotal, currency)}</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-600">Tax Rate (%):</span>
                  <Input
                    type="number"
                    value={taxRate}
                    onChange={(e) => setTaxRate(e.target.value)}
                    className="w-20 text-right"
                    step="0.01"
                    min="0"
                  />
                  <span className="font-medium w-32 text-right">{formatMoney(taxAmount, currency)}</span>
                </div>
                <div className="flex items-center gap-4 text-base font-bold border-t pt-2">
                  <span>Total:</span>
                  <span className="w-32 text-right">{formatMoney(total, currency)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 3: Terms & Conditions */}
      {step === 2 && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Terms & Conditions</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={termsAndConditions}
                onChange={(e) => setTermsAndConditions(e.target.value)}
                rows={12}
                placeholder="Enter terms and conditions..."
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Additional Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="Internal notes (not shown on PDF to client)..."
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 4: Review */}
      {step === 3 && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Review Your Bid</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Client & Bid Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">{labels.client}:</span>{' '}
                  <span className="font-medium">{selectedClient?.name || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Contact:</span>{' '}
                  <span className="font-medium">{selectedClient?.contact_name || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Title:</span>{' '}
                  <span className="font-medium">{title}</span>
                </div>
                <div>
                  <span className="text-gray-500">Valid Until:</span>{' '}
                  <span className="font-medium">
                    {formatDateOnly(validUntil, 'Not set')}
                  </span>
                </div>
              </div>

              {description && (
                <div>
                  <p className="text-sm text-gray-500">Description</p>
                  <p className="text-sm mt-1">{description}</p>
                </div>
              )}

              {/* Line Items Preview */}
              {lineItems.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Line Items ({lineItems.length})</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Type</th>
                          <th className="text-left px-3 py-2 font-medium">Description</th>
                          <th className="text-right px-3 py-2 font-medium">Qty</th>
                          <th className="text-left px-3 py-2 font-medium">Unit</th>
                          <th className="text-right px-3 py-2 font-medium">Price</th>
                          <th className="text-right px-3 py-2 font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sections.filter((s) => s.title.trim()).map((section) => {
                          const sectionItems = lineItems.filter(
                            (li) => li.sectionTempId === section.tempId
                          );
                          if (sectionItems.length === 0) return null;
                          return (
                            <Fragment key={section.tempId}>
                              <tr className="bg-gray-100">
                                <td colSpan={6} className="px-3 py-2 font-medium">
                                  {section.title}
                                </td>
                              </tr>
                              {sectionItems.map((item) => (
                                <LineItemPreviewRow key={item.tempId} item={item} />
                              ))}
                            </Fragment>
                          );
                        })}
                        {lineItems
                          .filter((li) => li.sectionTempId === null)
                          .map((item) => (
                            <LineItemPreviewRow key={item.tempId} item={item} />
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Gear Preview */}
              {gearLines.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Gear Estimate ({gearLines.length})</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Description</th>
                          <th className="text-left px-3 py-2 font-medium">Rate</th>
                          <th className="text-right px-3 py-2 font-medium">Qty</th>
                          <th className="text-left px-3 py-2 font-medium">Dates</th>
                          <th className="text-right px-3 py-2 font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gearLines.map((line) => {
                          const lineTotal = calculateGearLineTotal(line, estimatedStartDate, estimatedEndDate);
                          const rate = line.overrideRate.trim() !== ''
                            ? toNumber(line.overrideRate)
                            : toNumber(line.baseRate);
                          const displayStart = line.startDate || estimatedStartDate;
                          const displayEnd = line.endDate || estimatedEndDate;
                          return (
                            <Fragment key={line.tempId}>
                              <tr className="border-t">
                                <td className="px-3 py-2">{line.description || '—'}</td>
                                <td className="px-3 py-2 text-gray-600">
                                  {formatMoney(rate, currency)}{' '}
                                  {RATE_UNITS.find((u) => u.value === line.rateUnit)?.label || line.rateUnit}
                                  {line.overrideRate.trim() !== '' && (
                                    <span className="text-xs text-blue-600 ml-1">(override)</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right">{line.quantity}</td>
                                <td className="px-3 py-2 text-gray-600">
                                  {displayStart && displayEnd
                                    ? `${formatDateOnly(displayStart)} – ${formatDateOnly(displayEnd)}`
                                    : '—'}
                                </td>
                                <td className="px-3 py-2 text-right font-medium">{formatMoney(lineTotal, currency)}</td>
                              </tr>
                              {line.adjustments.length > 0 && (
                                <tr className="bg-gray-50">
                                  <td colSpan={5} className="px-3 py-2">
                                    <span className="text-xs text-gray-500">Adjustments:</span>
                                    <ul className="text-xs text-gray-600 list-disc ml-4">
                                      {line.adjustments.map((adj) => (
                                        <li key={adj.tempId}>
                                          {adj.adjustment_type === 'discount' ? 'Discount' : 'Allowance'}: {adj.description || '—'} ({formatMoney(toNumber(adj.amount), currency)})
                                        </li>
                                      ))}
                                    </ul>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Totals */}
              <div className="flex flex-col items-end gap-1 text-sm">
                {lineItems.length > 0 && (
                  <div className="flex gap-8">
                    <span className="text-gray-600">Line Items Subtotal:</span>
                    <span className="font-medium">{formatMoney(lineSubtotal, currency)}</span>
                  </div>
                )}
                {gearLines.length > 0 && (
                  <div className="flex gap-8">
                    <span className="text-gray-600">Gear Subtotal:</span>
                    <span className="font-medium">{formatMoney(gearSubtotal, currency)}</span>
                  </div>
                )}
                <div className="flex gap-8">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">{formatMoney(subtotal, currency)}</span>
                </div>
                {toNumber(taxRate) > 0 && (
                  <div className="flex gap-8">
                    <span className="text-gray-600">Tax ({taxRate}%):</span>
                    <span className="font-medium">{formatMoney(taxAmount, currency)}</span>
                  </div>
                )}
                <div className="flex gap-8 text-base font-bold border-t pt-1 mt-1">
                  <span>Total:</span>
                  <span>{formatMoney(total, currency)}</span>
                </div>
              </div>

              {/* Terms Preview */}
              {termsAndConditions && (
                <div>
                  <h4 className="font-medium mb-1">Terms & Conditions</h4>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{termsAndConditions}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={() => (step === 0 ? navigate('/projects/bids') : setStep(step - 1))}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {step === 0 ? 'Cancel' : 'Back'}
        </Button>
        {step < 3 ? (
          <Button type="button" onClick={() => setStep(step + 1)} disabled={!canProceed()}>
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={createBidMutation.isPending || updateBidMutation.isPending}
          >
            {isEdit
              ? updateBidMutation.isPending
                ? 'Saving...'
                : 'Save Changes'
              : createBidMutation.isPending
                ? 'Creating Bid...'
                : 'Create Bid'}
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function LineItemEditor({
  item,
  onUpdate,
  onRemove,
}: {
  item: LineItemRow;
  onUpdate: (tempId: string, field: keyof LineItemRow, value: string) => void;
  onRemove: (tempId: string) => void;
}) {
  const currency = useOrgCurrency();
  const lineTotal = toNumber(item.quantity) * toNumber(item.unit_price);

  return (
    <div className="flex items-start gap-2 p-2 border rounded-md bg-white">
      <Select
        value={item.line_type}
        onValueChange={(v) => onUpdate(item.tempId, 'line_type', v)}
      >
        <SelectTrigger className="w-[120px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {LINE_TYPES.map((lt) => (
            <SelectItem key={lt.value} value={lt.value}>
              {lt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        value={item.description}
        onChange={(e) => onUpdate(item.tempId, 'description', e.target.value)}
        placeholder="Description"
        className="flex-1"
      />
      <Input
        type="number"
        value={item.quantity}
        onChange={(e) => onUpdate(item.tempId, 'quantity', e.target.value)}
        className="w-20 text-right"
        step="0.01"
        min="0"
      />
      <Input
        value={item.unit}
        onChange={(e) => onUpdate(item.tempId, 'unit', e.target.value)}
        placeholder="unit"
        className="w-20"
      />
      <Input
        type="number"
        value={item.unit_price}
        onChange={(e) => onUpdate(item.tempId, 'unit_price', e.target.value)}
        className="w-28 text-right"
        step="0.01"
        min="0"
      />
      <Badge variant="secondary" className="whitespace-nowrap min-w-[80px] justify-center">
        {formatMoney(lineTotal, currency)}
      </Badge>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => onRemove(item.tempId)}
      >
        <Trash2 className="h-4 w-4 text-red-500" />
      </Button>
    </div>
  );
}

function LineItemPreviewRow({ item }: { item: LineItemRow }) {
  const currency = useOrgCurrency();
  const lineTotal = toNumber(item.quantity) * toNumber(item.unit_price);
  const typeLabel = LINE_TYPES.find((lt) => lt.value === item.line_type)?.label || item.line_type;

  return (
    <tr className="border-t">
      <td className="px-3 py-2 text-gray-600">{typeLabel}</td>
      <td className="px-3 py-2">{item.description}</td>
      <td className="px-3 py-2 text-right">{item.quantity}</td>
      <td className="px-3 py-2 text-gray-600">{item.unit}</td>
      <td className="px-3 py-2 text-right">{formatMoney(toNumber(item.unit_price), currency)}</td>
      <td className="px-3 py-2 text-right font-medium">{formatMoney(lineTotal, currency)}</td>
    </tr>
  );
}

function GearLineEditor({
  line,
  gearRates,
  availability,
  estimateStartDate,
  estimateEndDate,
  onUpdate,
  onRemove,
  onAddAdjustment,
  onUpdateAdjustment,
  onRemoveAdjustment,
}: {
  line: GearLineRow;
  gearRates: GearRate[];
  availability?: { is_scarce_warning: boolean; date_warning: boolean; total_owned: number; committed: number; available: number };
  estimateStartDate: string;
  estimateEndDate: string;
  onUpdate: (tempId: string, field: keyof GearLineRow, value: string | number | GearAdjustmentRow[]) => void;
  onRemove: (tempId: string) => void;
  onAddAdjustment: (tempId: string) => void;
  onUpdateAdjustment: (
    lineTempId: string,
    adjTempId: string,
    field: keyof GearAdjustmentRow,
    value: string | boolean
  ) => void;
  onRemoveAdjustment: (lineTempId: string, adjTempId: string) => void;
}) {
  const currency = useOrgCurrency();
  const lineTotal = calculateGearLineTotal(line, estimateStartDate, estimateEndDate);
  const selectedRate = gearRates.find((r) => r.id.toString() === line.gearRateId);

  return (
    <div className="border rounded-lg p-4 space-y-4 bg-white">
      <div className="flex items-start gap-2">
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="sm:col-span-2 lg:col-span-2">
            <Label>Description</Label>
            <Input
              value={line.description}
              onChange={(e) => onUpdate(line.tempId, 'description', e.target.value)}
              placeholder="e.g., Rope access equipment package"
            />
          </div>
          <div>
            <Label>Gear Rate</Label>
            <Select
              value={line.gearRateId}
              onValueChange={(v) => onUpdate(line.tempId, 'gearRateId', v)}
            >
              <SelectTrigger aria-label="Gear rate">
                <SelectValue placeholder="Select a rate..." />
              </SelectTrigger>
              <SelectContent>
                {gearRates.map((rate) => (
                  <SelectItem key={rate.id} value={rate.id.toString()}>
                    {rate.name}{' '}
                    {rate.gear_model_name || rate.gear_request_template_name
                      ? `(${rate.gear_model_name || rate.gear_request_template_name})`
                      : ''}{' '}
                    — {formatMoney(toNumber(rate.rate), currency)} / {rate.rate_unit.replace('_', ' ')}
                    {rate.rate_unit === 'hourly' && ` (≈ ${formatMoney(toNumber(rate.rate) * HOURS_PER_DAY, currency)}/day @ ${HOURS_PER_DAY} hrs)`}
                    {rate.is_default && ' • default'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Quantity</Label>
            <Input
              type="number"
              value={line.quantity}
              onChange={(e) => onUpdate(line.tempId, 'quantity', e.target.value)}
              aria-label="Gear quantity"
              className="text-right"
              step="0.01"
              min="0"
            />
          </div>
          <div>
            <Label>Base Rate</Label>
            <Input
              type="number"
              value={line.baseRate}
              onChange={(e) => onUpdate(line.tempId, 'baseRate', e.target.value)}
              className="text-right"
              step="0.01"
              min="0"
              disabled={!!selectedRate}
            />
          </div>
          <div>
            <Label>Override Rate</Label>
            <Input
              type="number"
              value={line.overrideRate}
              onChange={(e) => onUpdate(line.tempId, 'overrideRate', e.target.value)}
              aria-label="Override rate"
              className="text-right"
              step="0.01"
              min="0"
              placeholder={line.baseRate}
            />
          </div>
          <div>
            <Label>Rate Unit</Label>
            <Select
              value={line.rateUnit}
              onValueChange={(v) => onUpdate(line.tempId, 'rateUnit', v as BidGearRateUnit)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RATE_UNITS.map((u) => (
                  <SelectItem key={u.value} value={u.value}>
                    {u.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Start Date</Label>
            <Input
              type="date"
              value={line.startDate}
              onChange={(e) => onUpdate(line.tempId, 'startDate', e.target.value)}
            />
          </div>
          <div>
            <Label>End Date</Label>
            <Input
              type="date"
              value={line.endDate}
              onChange={(e) => onUpdate(line.tempId, 'endDate', e.target.value)}
            />
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 min-w-[100px]">
          <Badge variant="secondary" className="whitespace-nowrap justify-center">
            {formatMoney(lineTotal, currency)}
          </Badge>
          <Button type="button" variant="ghost" size="icon" aria-label="Remove gear line" onClick={() => onRemove(line.tempId)}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      </div>

      {availability?.is_scarce_warning && (
        <Alert className="border-yellow-500 text-yellow-800 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription>
            Scarcity advisory: {availability.committed} of {availability.total_owned} units committed
            ({availability.available} available). Dates or availability may change and will not block save.
          </AlertDescription>
        </Alert>
      )}

      {availability?.date_warning && !availability.is_scarce_warning && (
        <Alert>
          <AlertDescription>
            Availability numbers are uncertain because date information is incomplete. Please verify
            manually.
          </AlertDescription>
        </Alert>
      )}

      <div className="border rounded-md p-3 bg-gray-50 space-y-3">
        <div className="flex items-center justify-between">
          <h5 className="text-sm font-medium">Allowance / Discount</h5>
          <Button type="button" variant="outline" size="sm" onClick={() => onAddAdjustment(line.tempId)}>
            <Plus className="h-3 w-3 mr-1" />
            Add Adjustment
          </Button>
        </div>
        {line.adjustments.length === 0 ? (
          <p className="text-sm text-gray-400">No adjustments. Click Add Adjustment to apply an allowance or discount.</p>
        ) : (
          <div className="space-y-2">
            {line.adjustments.map((adj) => (
              <div key={adj.tempId} className="flex items-start gap-2">
                <Select
                  value={adj.adjustment_type}
                  onValueChange={(v) =>
                    onUpdateAdjustment(line.tempId, adj.tempId, 'adjustment_type', v)
                  }
                >
                  <SelectTrigger className="w-[120px]" aria-label="Adjustment type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="allowance">Allowance</SelectItem>
                    <SelectItem value="discount">Discount</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  value={adj.description}
                  onChange={(e) =>
                    onUpdateAdjustment(line.tempId, adj.tempId, 'description', e.target.value)
                  }
                  placeholder="Description"
                  className="flex-1"
                />
                <Input
                  type="number"
                  value={adj.amount}
                  onChange={(e) =>
                    onUpdateAdjustment(line.tempId, adj.tempId, 'amount', e.target.value)
                  }
                  placeholder="Amount"
                  aria-label="Adjustment amount"
                  className="w-28 text-right"
                  step="0.01"
                  min="0"
                />
                <Input
                  type="number"
                  value={adj.quantity}
                  onChange={(e) =>
                    onUpdateAdjustment(line.tempId, adj.tempId, 'quantity', e.target.value)
                  }
                  aria-label="Adjustment quantity"
                  className="w-20 text-right"
                  step="0.01"
                  min="0"
                />
                <div className="flex items-center gap-2 min-w-[80px]">
                  <Checkbox
                    id={`per-unit-${line.tempId}-${adj.tempId}`}
                    checked={adj.is_per_unit}
                    onCheckedChange={(v) =>
                      onUpdateAdjustment(line.tempId, adj.tempId, 'is_per_unit', v === true)
                    }
                  />
                  <Label htmlFor={`per-unit-${line.tempId}-${adj.tempId}`} className="text-xs whitespace-nowrap">
                    Per unit
                  </Label>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Remove adjustment"
                  onClick={() => onRemoveAdjustment(line.tempId, adj.tempId)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
